import { Eye, EyeSlash, MagnifyingGlass, X } from '@phosphor-icons/react';
import {
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { t } from '../../i18n';

/**
 * Champ de saisie avec son libelle et son message d'erreur.
 * `tone="brand"` donne l'habillage clair des ecrans de connexion et
 * d'inscription ; `tone="spot"` celui de l'interieur de l'application.
 */
export function Field({
  label,
  error,
  hint,
  tone = 'brand',
  className = '',
  ...rest
}: {
  label?: string;
  error?: string | null;
  hint?: ReactNode;
  tone?: 'brand' | 'spot';
} & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  /*
    Un mot de passe se tape a l'aveugle et se rate sur un telephone. L'oeil
    n'apparait qu'une fois la saisie commencee : un formulaire vide n'a rien
    a devoiler, et c'est ce qui le garde discret.
  */
  const [devoile, setDevoile] = useState(false);
  const cache = rest.type === 'password';
  const rempli = String(rest.value ?? '').length > 0;
  const oeil = cache && rempli;

  return (
    <div className={`field field--${tone} ${className}`.trim()}>
      {label && (
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
      )}
      <div className="field__box">
        <input
          id={id}
          {...rest}
          type={cache && devoile ? 'text' : rest.type}
          className={`field__input${error ? ' field__input--error' : ''}${
            oeil ? ' field__input--oeil' : ''
          }`}
          aria-invalid={error ? true : undefined}
        />
        {oeil && (
          <button
            type="button"
            className="field__oeil"
            aria-label={devoile ? t.composants.champ.masquer : t.composants.champ.afficher}
            aria-pressed={devoile}
            /* Le doigt ne prend pas le focus au champ : sans cela le
               clavier se referme et il faut retoucher la ligne. */
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setDevoile((v) => !v)}
          >
            {devoile ? <EyeSlash size={19} /> : <Eye size={19} />}
          </button>
        )}
      </div>
      {error && <p className="field__error">{error}</p>}
      {!error && hint}
    </div>
  );
}

/** Zone de texte : description d'un evenement, d'un groupe. */
export function TextArea({
  label,
  error,
  tone = 'spot',
  className = '',
  ...rest
}: {
  label?: string;
  error?: string | null;
  tone?: 'brand' | 'spot';
} & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <div className={`field field--${tone} ${className}`.trim()}>
      {label && (
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
      )}
      <textarea
        id={id}
        className={`field__input field__input--area${error ? ' field__input--error' : ''}`}
        {...rest}
      />
      {error && <p className="field__error">{error}</p>}
    </div>
  );
}

/**
 * Barre de recherche a loupe.
 * On la retrouve sur "Choisis ton spot", les evenements et l'ajout d'amis.
 */
export function SearchField({
  tone = 'spot',
  className = '',
  onClear,
  ...rest
}: {
  tone?: 'brand' | 'spot';
  /**
   * Fournir cette fonction fait apparaitre une croix des qu'il y a
   * quelque chose a effacer. Sans elle, le champ reste nu.
   */
  onClear?: () => void;
} & InputHTMLAttributes<HTMLInputElement>) {
  const filled = String(rest.value ?? '').length > 0;

  return (
    <div className={`search search--${tone} ${className}`.trim()}>
      <MagnifyingGlass size={20} weight="bold" className="search__icon" />
      <input type="search" className="search__input" {...rest} />

      {onClear && filled && (
        <button
          type="button"
          className="search__clear"
          aria-label={t.composants.champ.effacer}
          /*
            Le doigt ne prend pas le focus au champ : sans cela, effacer
            referme le clavier, et il faut retoucher la barre pour
            reprendre sa recherche.
          */
          onMouseDown={(event) => event.preventDefault()}
          onClick={onClear}
        >
          <X size={15} weight="bold" />
        </button>
      )}
    </div>
  );
}

/**
 * Interrupteur des ecrans de reglages.
 * Il est pilote par le parent : l'etat vit dans l'ecran, pas ici.
 */
export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Nom lu par les lecteurs d'ecran, le libelle visible est a cote. */
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={`toggle${checked ? ' toggle--on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className="toggle__knob" />
    </button>
  );
}

/** Bouton radio des listes de choix ("Tout le serveur" / "Mes amis"). */
export function Radio({ checked, label }: { checked: boolean; label: string }) {
  return (
    <span
      className={`radio${checked ? ' radio--on' : ''}`}
      role="radio"
      aria-checked={checked}
      aria-label={label}
    />
  );
}

/** Case a cocher carree : conditions generales, selection de membres. */
export function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      className={`checkbox${checked ? ' checkbox--on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      {checked && <Coche />}
    </button>
  );
}

/**
 * La coche seule, sans commande.
 *
 * Elle sert la ou c'est la ligne entiere qui coche — une liste de gens a
 * mettre dans un groupe se parcourt au pouce, et viser une case de vingt-
 * six pixels au bout de chaque ligne n'est pas un geste. La ligne porte
 * alors le role, et la case n'est plus qu'un dessin : deux commandes
 * imbriquees seraient injouables au clavier comme au lecteur d'ecran.
 */
export function CheckMark({ checked }: { checked: boolean }) {
  return (
    <span className={`checkbox${checked ? ' checkbox--on' : ''}`} aria-hidden="true">
      {checked && <Coche />}
    </span>
  );
}

/** Le trait de la coche, partage par la case et son dessin. */
function Coche() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
      <path
        d="M3 8.5 6.2 11.6 13 4.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
